import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

export default function ReportsPage() {
  const [sales, setSales]     = useState(null);
  const [prod, setProd]       = useState(null);
  const [att, setAtt]         = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [month, setMonth]     = useState(now.getMonth()+1);
  const [year, setYear]       = useState(now.getFullYear());

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/reports/sales/?month=${month}&year=${year}`),
      api.get(`/reports/production/?month=${month}&year=${year}`),
      api.get(`/reports/attendance/?month=${month}&year=${year}`),
    ]).then(([s,p,a]) => { setSales(s.data); setProd(p.data); setAtt(a.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [month, year]);

  const Row = ({ label, val, color }) => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom:'1px solid var(--border-light)' }}>
      <span style={{ fontSize:13, color:'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:700, color: color||'var(--text-primary)' }}>{val}</span>
    </div>
  );

  return (
    <Layout
      title="Reports"
      subtitle="Monthly analytics and insights"
      actions={
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <select className="form-input" style={{ width:'auto' }} value={month} onChange={e => setMonth(e.target.value)}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
              <option key={i} value={i+1}>{m}</option>
            ))}
          </select>
          <select className="form-input" style={{ width:'auto' }} value={year} onChange={e => setYear(e.target.value)}>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      }
    >
      {loading ? <div className="loading"><div className="spinner"/>Loading reports...</div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Sales */}
          {sales && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Sales Report — {sales.period}</div>
              </div>
              <div className="kpi-grid" style={{ marginBottom:16 }}>
                <div className="kpi-card brand"><div className="kpi-value">₹{Number(sales.orders?.total_value||0).toLocaleString('en-IN')}</div><div className="kpi-label">Total Value</div></div>
                <div className="kpi-card green"><div className="kpi-value">₹{Number(sales.orders?.collected||0).toLocaleString('en-IN')}</div><div className="kpi-label">Collected</div></div>
                <div className="kpi-card red"><div className="kpi-value">₹{Number(sales.orders?.pending||0).toLocaleString('en-IN')}</div><div className="kpi-label">Pending</div></div>
                <div className="kpi-card blue"><div className="kpi-value">{sales.orders?.total||0}</div><div className="kpi-label">Orders</div></div>
              </div>
              {sales.top_customers?.length > 0 && (
                <>
                  <div className="section-divider">Top Customers</div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Customer</th><th>Orders</th><th>Total</th></tr></thead>
                      <tbody>
                        {sales.top_customers.map((c,i) => (
                          <tr key={i}>
                            <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{c.customer__name}</td>
                            <td>{c.orders}</td>
                            <td style={{ color:'var(--brand)', fontWeight:600 }}>₹{Number(c.total||0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Production */}
          {prod && (
            <div className="card">
              <div className="card-header"><div className="card-title">Production Report — {prod.period}</div></div>
              <div className="grid-2">
                <div>
                  <div className="section-divider">Blocks</div>
                  <Row label="Total Registered" val={prod.blocks?.total||0}/>
                  <Row label="Received"          val={prod.blocks?.received||0}   color="var(--blue)"/>
                  <Row label="Completed"         val={prod.blocks?.completed||0}  color="var(--green)"/>
                  <Row label="Rejected"          val={prod.blocks?.rejected||0}   color="var(--red)"/>
                </div>
                <div>
                  <div className="section-divider">Slabs</div>
                  <Row label="Total"    val={prod.slabs?.total||0}/>
                  <Row label="Passed"   val={prod.slabs?.passed||0}   color="var(--green)"/>
                  <Row label="Rejected" val={prod.slabs?.rejected||0} color="var(--red)"/>
                  <Row label="Sold"     val={prod.slabs?.sold||0}     color="var(--amber)"/>
                </div>
              </div>
            </div>
          )}

          {/* Attendance */}
          {att && (
            <div className="card">
              <div className="card-header"><div className="card-title">Attendance Report — {att.period}</div></div>
              <div className="kpi-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom: att.by_employee?.length ? 16 : 0 }}>
                <div className="kpi-card green"><div className="kpi-value">{att.summary?.present_days||0}</div><div className="kpi-label">Present Days</div></div>
                <div className="kpi-card red"><div className="kpi-value">{att.summary?.absent_days||0}</div><div className="kpi-label">Absent Days</div></div>
                <div className="kpi-card brand"><div className="kpi-value">₹{Number(att.summary?.total_wages||0).toLocaleString('en-IN')}</div><div className="kpi-label">Total Wages</div></div>
              </div>
              {att.by_employee?.length > 0 && (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Employee</th><th>Present</th><th>Absent</th><th>Total Wage</th></tr></thead>
                    <tbody>
                      {att.by_employee.map((e,i) => (
                        <tr key={i}>
                          <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{e.employee__first_name} {e.employee__last_name}</td>
                          <td style={{ color:'var(--green)', fontWeight:600 }}>{e.present}</td>
                          <td style={{ color:'var(--red)', fontWeight:600 }}>{e.absent}</td>
                          <td style={{ color:'var(--brand)', fontWeight:600 }}>₹{Number(e.total_wage||0).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
